import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { switchMap, of, filter, map, distinctUntilChanged, Subscription, Subject, takeUntil, pairwise, take, EMPTY, startWith, tap } from 'rxjs';
import { AppState } from 'src/app/reducers';
import { HelperService } from 'src/app/service/helper.service';
import { AuthService } from '../service/auth.service';
import { AuthEffects } from '../state/auth.effects';
import { UserToken } from '../model/user.model';
import { Route } from 'src/app/model/session-tracking/tracking-type';

@Component({
    selector: 'app-session-tracker',
    templateUrl: './session-tracker.component.html',
    styleUrl: './session-tracker.component.scss'
})
export class SessionTrackerComponent implements OnInit, OnDestroy {
    userToken?: UserToken;
    private isLoggedInSub?: Subscription;
    private isLoggedInNavigatedSub?: Subscription;

    private destroy$ = new Subject<void>();

    private previousUrl: string | null = null;
    private currentUrl: string | null = null;

    constructor(
        private authService: AuthService,
        private authEffects: AuthEffects,
        private store: Store<AppState>,
        private helperService: HelperService,
        private router: Router
    ) {}

    ngOnInit() {
        this.isLoggedInSub = this.authEffects.isLoggedIn$
            .pipe(
                switchMap((isLoggedIn) => {
                    if (!isLoggedIn) return EMPTY;

                    const storedUserToken = localStorage.getItem('userToken');
                    if (!storedUserToken) return EMPTY;

                    this.userToken = JSON.parse(storedUserToken) as UserToken;

                    return this.router.events.pipe(
                        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                        map((event: NavigationEnd) => event.urlAfterRedirects),
                        distinctUntilChanged(),
                        startWith(null),
                        pairwise(),
                        switchMap(([previousUrl, currentUrl]) =>
                            this.authEffects.isLoggedInNavigated$.pipe(
                                filter(Boolean),
                                take(1), // only react once per navigation
                                map(() => [previousUrl, currentUrl] as const)
                            )
                        )
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(([previousUrl, currentUrl]) => {
                this.previousUrl = previousUrl;
                this.currentUrl = currentUrl;

                if (this.currentUrl) {
                    this.saveNewRouteToSessionTracking(this.currentUrl);
                }
            });
    }

    ngOnDestroy() {
        this.isLoggedInSub?.unsubscribe();
        this.isLoggedInNavigatedSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    saveNewRouteToSessionTracking(targetUrl: string, timedOut: boolean = false) {
        let newRoute = new Route();
        newRoute.path = targetUrl;
        newRoute.timeStamp = new Date().toLocaleString();

        this.helperService.addCurrSessionTrackingRoute(newRoute);
    }
}
